import type { ManualDecision, MatchResult, NumericTolerances } from '../../types'
import { finalizeDiffResult } from '../diff'
import { createAddedMatchResult, sortMatchResults } from './matcher'

function snapshot(result: MatchResult): NonNullable<MatchResult['preManualState']> {
  return {
    targetRow: result.targetRow,
    matchType: result.matchType,
    confidence: result.confidence,
    matchReason: result.matchReason,
    confirmationState: result.confirmationState,
    status: result.status,
  }
}

function candidateTarget(result: MatchResult, targetRowId: string) {
  if (result.targetRow?.id === targetRowId) return result.targetRow
  return (
    result.candidates.find((candidate) => candidate.targetRow.id === targetRowId)?.targetRow ?? null
  )
}

export function applyManualDecision(
  result: MatchResult,
  decision: ManualDecision,
  tolerances: Partial<NumericTolerances> = {},
): MatchResult {
  if (result.status !== 'pending') throw new Error('只有待确认匹配可以应用人工决定')
  const preManualState = snapshot(result)

  if (decision.kind === 'confirm-match') {
    const targetRow = candidateTarget(result, decision.targetRowId)
    if (targetRow === null) throw new Error('所选候选不属于该待确认项目')
    return finalizeDiffResult(
      {
        ...result,
        targetRow,
        matchType: 'manual',
        confidence:
          result.candidates.find((candidate) => candidate.targetRow.id === targetRow.id)?.score ??
          result.confidence,
        matchReason: '人工确认匹配',
        confirmationState: 'confirmed',
        manualDecision: { kind: decision.kind, targetRowId: targetRow.id },
        preManualState,
      },
      tolerances,
    )
  }

  if (decision.kind === 'mark-removed') {
    return finalizeDiffResult(
      {
        ...result,
        targetRow: null,
        matchType: 'manual',
        confidence: 1,
        matchReason: '人工判定为基准版删除',
        confirmationState: 'confirmed',
        manualDecision: { kind: decision.kind, targetRowId: null },
        preManualState,
      },
      tolerances,
    )
  }

  return finalizeDiffResult(
    {
      ...result,
      matchReason: `${result.matchReason}；人工暂时跳过`,
      confirmationState: 'skipped',
      manualDecision: { kind: decision.kind, targetRowId: null },
      preManualState,
    },
    tolerances,
  )
}

export function undoManualDecision(
  result: MatchResult,
  tolerances: Partial<NumericTolerances> = {},
): MatchResult {
  if (result.preManualState === null) return result
  const previous = result.preManualState
  return finalizeDiffResult(
    {
      ...result,
      targetRow: previous.targetRow,
      matchType: previous.matchType,
      confidence: previous.confidence,
      matchReason: previous.matchReason,
      confirmationState: previous.confirmationState,
      status: previous.status,
      manualDecision: null,
      preManualState: null,
    },
    tolerances,
  )
}

export function confirmPendingMatch(
  result: MatchResult,
  targetRowId: string,
  tolerances: Partial<NumericTolerances> = {},
): MatchResult {
  return applyManualDecision(result, { kind: 'confirm-match', targetRowId }, tolerances)
}

export function markPendingAsRemoved(
  result: MatchResult,
  tolerances: Partial<NumericTolerances> = {},
): MatchResult {
  return applyManualDecision(result, { kind: 'mark-removed' }, tolerances)
}

export function skipPendingMatch(
  result: MatchResult,
  tolerances: Partial<NumericTolerances> = {},
): MatchResult {
  return applyManualDecision(result, { kind: 'skip' }, tolerances)
}

export function applyManualDecisionToResults(
  results: readonly MatchResult[],
  resultId: string,
  decision: ManualDecision,
  tolerances: Partial<NumericTolerances> = {},
): MatchResult[] {
  const current = results.find((result) => result.id === resultId)
  if (current === undefined) throw new Error('找不到待处理的匹配结果')
  const selectedTarget =
    decision.kind === 'confirm-match' ? candidateTarget(current, decision.targetRowId) : null
  if (decision.kind === 'confirm-match' && selectedTarget === null) {
    throw new Error('所选候选不属于该待确认项目')
  }
  if (selectedTarget !== null) {
    const conflict = results.find(
      (result) =>
        result.id !== current.id &&
        result.targetRow?.id === selectedTarget.id &&
        result.status !== 'added',
    )
    if (conflict !== undefined) throw new Error('所选候选已被其他项目占用')
  }

  const releasedTarget =
    decision.kind === 'skip' || current.targetRow?.id === selectedTarget?.id
      ? null
      : current.targetRow
  const updated = applyManualDecision(current, decision, tolerances)
  const next = results
    .filter(
      (result) =>
        result.id !== current.id &&
        !(
          selectedTarget !== null &&
          result.status === 'added' &&
          result.targetRow?.id === selectedTarget.id
        ),
    )
    .concat(updated)
  if (
    releasedTarget !== null &&
    !next.some((result) => result.targetRow?.id === releasedTarget.id)
  ) {
    next.push(createAddedMatchResult(releasedTarget, tolerances))
  }
  return sortMatchResults(next)
}

export function undoManualDecisionInResults(
  results: readonly MatchResult[],
  resultId: string,
  tolerances: Partial<NumericTolerances> = {},
): MatchResult[] {
  const current = results.find((result) => result.id === resultId)
  if (!current?.preManualState) return [...results]
  const currentTarget = current.targetRow
  const previousTarget = current.preManualState.targetRow
  const restored = undoManualDecision(current, tolerances)
  const next = results
    .filter(
      (result) =>
        result.id !== current.id &&
        !(
          previousTarget !== null &&
          result.status === 'added' &&
          result.targetRow?.id === previousTarget.id
        ),
    )
    .concat(restored)
  if (
    currentTarget !== null &&
    currentTarget.id !== previousTarget?.id &&
    !next.some((result) => result.targetRow?.id === currentTarget.id)
  ) {
    next.push(createAddedMatchResult(currentTarget, tolerances))
  }
  return sortMatchResults(next)
}
